import { useCallback, useEffect, useMemo, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { PageShell } from "@/components/mai/PageShell";
import { useProduct } from "@/context/ProductContext";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { CircleNotch, Plus, Trash, CheckCircle, WarningCircle, X, LinkSimple, BookOpen, Warning, Copy, Prohibit, Files, ArrowSquareOut } from "@phosphor-icons/react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const CATEGORY_LABEL = {
  clinical: "Clinical",
  regulatory: "Regulatory",
  epidemiological: "Epidemiological",
  economic: "Economic",
  pricing: "Pricing",
  competitor: "Competitor",
  country_requirement: "Country requirement",
};
const STATUS_TONE = {
  verified: "bg-emerald-50 text-emerald-700 border-emerald-200",
  pending: "bg-slate-100 text-slate-600 border-slate-200",
  broken: "bg-red-50 text-red-700 border-red-200",
  mismatch: "bg-amber-50 text-amber-700 border-amber-300",
};

function StatusChip({ status, testId }) {
  return <span data-testid={testId} className={`inline-block text-[9px] uppercase tracking-widest font-bold px-1.5 py-0.5 border ${STATUS_TONE[status] || STATUS_TONE.pending}`}>{status}</span>;
}

function SourcePanel({ claim, source, onClose, onChange, productId }) {
  const [busy, setBusy] = useState(false);
  const verify = async (status) => {
    setBusy(true);
    try {
      await axios.post(`${API}/claims/${claim.id}/sources/${source.id}/verify`, { status });
      toast.success(`Source marked ${status}`);
      onChange();
    } catch (e) { toast.error("Verification failed"); }
    finally { setBusy(false); }
  };
  const remove = async () => {
    if (!window.confirm("Remove this source link?")) return;
    setBusy(true);
    try {
      await axios.delete(`${API}/claims/${claim.id}/sources/${source.id}`);
      toast.success("Source removed"); onChange(); onClose();
    } catch (e) { toast.error("Remove failed"); }
    finally { setBusy(false); }
  };
  return (
    <div data-testid="source-panel" className="fixed inset-0 z-50 flex" onClick={onClose}>
      <div className="flex-1 bg-black/40" />
      <div onClick={(e) => e.stopPropagation()} className="w-[520px] max-w-full bg-white border-l border-slate-200 overflow-y-auto">
        <header className="px-5 py-4 border-b border-slate-200 flex items-start justify-between">
          <div className="min-w-0">
            <p className="overline-label text-slate-400">Source panel</p>
            <p className="font-heading font-bold text-sm mt-0.5 truncate">{source.source_document}</p>
          </div>
          <button data-testid="source-panel-close" onClick={onClose} className="text-slate-400 hover:text-slate-900 p-1"><X size={16} /></button>
        </header>
        <div className="px-5 py-4 space-y-4">
          <div>
            <p className="overline-label text-slate-400">Statement in report</p>
            <p className="text-xs text-slate-700 border-l-2 border-slate-300 pl-2 mt-1 italic">&ldquo;{claim.statement}&rdquo;</p>
          </div>
          <div>
            <p className="overline-label text-slate-400">Exact supporting passage</p>
            <p className="text-xs text-slate-800 bg-yellow-50/60 border border-yellow-100 p-2 mt-1 leading-relaxed">{source.exact_passage}</p>
            {source.page_or_section && <p className="text-[10px] text-slate-400 mt-1">Location: {source.page_or_section}</p>}
          </div>
          <div>
            <p className="overline-label text-slate-400">Complete reference</p>
            <p className="text-xs text-slate-700 mt-1">{source.complete_reference}</p>
          </div>
          <div className="grid grid-cols-2 gap-3 text-[11px]">
            {source.doi && <div><p className="overline-label text-slate-400">DOI</p><p className="text-slate-700 font-mono-data">{source.doi}</p></div>}
            {source.access_date && <div><p className="overline-label text-slate-400">Access date</p><p className="text-slate-700">{source.access_date}</p></div>}
            {source.year && <div><p className="overline-label text-slate-400">Year</p><p className="text-slate-700">{source.year}</p></div>}
            {source.journal && <div><p className="overline-label text-slate-400">Journal</p><p className="text-slate-700">{source.journal}</p></div>}
          </div>
          {source.source_url && (
            <a data-testid="source-url" href={source.source_url} target="_blank" rel="noreferrer"
              className="inline-flex items-center gap-1 text-xs text-blue-700 font-semibold hover:underline">
              Open source URL <ArrowSquareOut size={11} />
            </a>
          )}
          <div className="pt-3 border-t border-slate-200">
            <p className="overline-label text-slate-400">Verification</p>
            <div className="flex items-center gap-2 mt-1 text-[11px]">
              <StatusChip status={source.citation_status} />
              {source.reviewer_email && <span className="text-slate-500">by {source.reviewer_email}</span>}
              {source.verification_date && <span className="text-slate-400">· {new Date(source.verification_date).toLocaleString()}</span>}
            </div>
            <div className="flex flex-wrap gap-2 mt-3">
              {["verified", "broken", "mismatch", "pending"].map((s) => (
                <button key={s} data-testid={`verify-${s}`} disabled={busy || source.citation_status === s} onClick={() => verify(s)}
                  className={`text-[11px] font-semibold px-2.5 py-1.5 border ${source.citation_status === s ? "bg-slate-900 text-white border-slate-900" : "bg-white border-slate-300 text-slate-600 hover:bg-slate-50"} disabled:opacity-50`}>
                  Mark {s}
                </button>
              ))}
              <button data-testid="source-remove" onClick={remove} disabled={busy}
                className="text-[11px] font-semibold text-red-600 border border-red-200 px-2.5 py-1.5 hover:bg-red-50 ml-auto flex items-center gap-1 disabled:opacity-50">
                <Trash size={11} /> Remove
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

function ClaimForm({ productId, onSaved, onCancel, initial }) {
  const [category, setCategory] = useState(initial?.category || "clinical");
  const [statement, setStatement] = useState(initial?.statement || "");
  const [countryId, setCountryId] = useState(initial?.country_id || "");
  const [link, setLink] = useState({
    exact_passage: "", page_or_section: "", source_document: "",
    complete_reference: "", doi: "", source_url: "", access_date: "",
    authors: "", year: "", title: "", journal: "",
    volume: "", issue: "", pages: "",
    citation_status: "pending",
  });
  const [busy, setBusy] = useState(false);
  const submit = async () => {
    if (statement.trim().length < 5) return toast.error("Statement is required (min 5 chars)");
    if (link.exact_passage.trim().length < 5) return toast.error("Exact passage is required (min 5 chars)");
    if (!link.source_document.trim()) return toast.error("Source document is required");
    if (link.complete_reference.trim().length < 8) return toast.error("Complete reference is required");
    setBusy(true);
    try {
      const payload = {
        product_id: productId, category, statement: statement.trim(),
        country_id: countryId || null,
        source_links: [{
          ...link,
          authors: link.authors ? link.authors.split(/[,;]/).map((s) => s.trim()).filter(Boolean) : [],
          year: link.year ? parseInt(link.year, 10) : null,
        }],
      };
      await axios.post(`${API}/claims`, payload);
      toast.success("Claim created");
      onSaved();
    } catch (e) { toast.error(e.response?.data?.detail || "Create failed"); }
    finally { setBusy(false); }
  };
  return (
    <div data-testid="claim-form" className="bg-white border border-slate-200 p-5 space-y-3">
      <p className="overline-label text-slate-400">New claim</p>
      <div className="grid grid-cols-2 gap-3">
        <div>
          <p className="overline-label text-slate-400">Category</p>
          <Select value={category} onValueChange={setCategory}>
            <SelectTrigger data-testid="form-category" className="h-8 text-xs bg-white"><SelectValue /></SelectTrigger>
            <SelectContent className="bg-white">
              {Object.entries(CATEGORY_LABEL).map(([k, l]) => <SelectItem key={k} value={k}>{l}</SelectItem>)}
            </SelectContent>
          </Select>
        </div>
        <div>
          <p className="overline-label text-slate-400">Country id (optional)</p>
          <Input data-testid="form-country" className="h-8 text-xs" value={countryId} onChange={(e) => setCountryId(e.target.value)} placeholder="e.g. japan" />
        </div>
      </div>
      <div>
        <p className="overline-label text-slate-400">Statement (as it appears in the report)</p>
        <Textarea data-testid="form-statement" rows={2} className="text-xs" value={statement} onChange={(e) => setStatement(e.target.value)} />
      </div>
      <div className="border-t border-slate-100 pt-3">
        <p className="overline-label text-slate-400 mb-1">Source link</p>
        <Input data-testid="form-source-doc" className="h-8 text-xs mb-2" placeholder="Source document (e.g. Landmark RCT 2023 publication)" value={link.source_document} onChange={(e) => setLink({ ...link, source_document: e.target.value })} />
        <Textarea data-testid="form-passage" rows={2} className="text-xs mb-2" placeholder="Exact supporting passage" value={link.exact_passage} onChange={(e) => setLink({ ...link, exact_passage: e.target.value })} />
        <div className="grid grid-cols-2 gap-2">
          <Input data-testid="form-page" className="h-8 text-xs" placeholder="Page / section (e.g. Results p. 12)" value={link.page_or_section} onChange={(e) => setLink({ ...link, page_or_section: e.target.value })} />
          <Input data-testid="form-access" className="h-8 text-xs" placeholder="Access date YYYY-MM-DD" value={link.access_date} onChange={(e) => setLink({ ...link, access_date: e.target.value })} />
          <Input className="h-8 text-xs" placeholder="Authors (comma sep)" value={link.authors} onChange={(e) => setLink({ ...link, authors: e.target.value })} />
          <Input className="h-8 text-xs" placeholder="Year" value={link.year} onChange={(e) => setLink({ ...link, year: e.target.value })} />
          <Input className="h-8 text-xs" placeholder="Title" value={link.title} onChange={(e) => setLink({ ...link, title: e.target.value })} />
          <Input className="h-8 text-xs" placeholder="Journal" value={link.journal} onChange={(e) => setLink({ ...link, journal: e.target.value })} />
          <Input className="h-8 text-xs" placeholder="Volume" value={link.volume} onChange={(e) => setLink({ ...link, volume: e.target.value })} />
          <Input className="h-8 text-xs" placeholder="Issue" value={link.issue} onChange={(e) => setLink({ ...link, issue: e.target.value })} />
          <Input className="h-8 text-xs" placeholder="Pages" value={link.pages} onChange={(e) => setLink({ ...link, pages: e.target.value })} />
          <Input data-testid="form-doi" className="h-8 text-xs" placeholder="DOI (e.g. 10.1056/NEJMoa2200000)" value={link.doi} onChange={(e) => setLink({ ...link, doi: e.target.value })} />
          <Input data-testid="form-url" className="h-8 text-xs" placeholder="Source URL" value={link.source_url} onChange={(e) => setLink({ ...link, source_url: e.target.value })} />
        </div>
        <Textarea data-testid="form-reference" rows={2} className="text-xs mt-2" placeholder="Complete reference (bibliographic entry)" value={link.complete_reference} onChange={(e) => setLink({ ...link, complete_reference: e.target.value })} />
      </div>
      <div className="flex justify-end gap-2 pt-2">
        <button onClick={onCancel} className="text-[11px] font-semibold text-slate-600 border border-slate-300 px-3 py-1.5 hover:bg-slate-50">Cancel</button>
        <button data-testid="form-submit" onClick={submit} disabled={busy}
          className="text-[11px] font-semibold text-white bg-slate-900 px-3 py-1.5 hover:bg-slate-800 disabled:opacity-50 flex items-center gap-1.5">
          {busy ? <CircleNotch size={11} className="animate-spin" /> : <Plus size={11} />} Create claim
        </button>
      </div>
    </div>
  );
}

function ClaimRow({ claim, onOpen, onOpenSource }) {
  const status = claim.source_links.length === 0
    ? "missing" : claim.source_links.every((l) => l.citation_status === "verified") ? "all_verified" : "partial";
  return (
    <div data-testid={`claim-row-${claim.id}`} className="bg-white border border-slate-200 p-4 flex flex-col gap-2">
      <div className="flex items-start justify-between gap-2">
        <div className="min-w-0">
          <p className="overline-label text-slate-400">{CATEGORY_LABEL[claim.category] || claim.category}
            {claim.country_id && <span className="ml-2 text-slate-400">· {claim.country_id}</span>}</p>
          <p className="text-xs font-semibold text-slate-800 mt-1 leading-relaxed">{claim.statement}</p>
        </div>
        {status === "missing" ? <StatusChip status="broken" testId={`claim-status-${claim.id}`} /> :
          status === "all_verified" ? <StatusChip status="verified" testId={`claim-status-${claim.id}`} /> :
            <StatusChip status="pending" testId={`claim-status-${claim.id}`} />}
      </div>
      {claim.source_links.length === 0 ? (
        <p data-testid={`missing-${claim.id}`} className="text-[11px] text-red-600 border border-dashed border-red-200 p-2">
          Missing citation — link at least one source to lift this warning.
        </p>
      ) : (
        <ul className="space-y-1">
          {claim.source_links.map((l) => (
            <li key={l.id}>
              <button data-testid={`source-${l.id}`} onClick={() => onOpenSource(claim, l)}
                className="w-full text-left flex items-center gap-2 border border-slate-200 hover:border-slate-900 bg-slate-50/40 px-2 py-1.5 text-[11px]">
                <LinkSimple size={11} className="text-slate-400 shrink-0" />
                <span className="font-semibold text-slate-700 truncate flex-1">{l.source_document}</span>
                {l.page_or_section && <span className="text-[10px] text-slate-400 truncate max-w-[100px]">{l.page_or_section}</span>}
                <StatusChip status={l.citation_status} />
              </button>
            </li>
          ))}
        </ul>
      )}
      <button data-testid={`open-claim-${claim.id}`} onClick={() => onOpen(claim)}
        className="self-end text-[10px] text-slate-500 hover:text-slate-900 font-semibold">Edit / add source →</button>
    </div>
  );
}

function LintPanel({ productId }) {
  const [d, setD] = useState(null);
  const [loading, setLoading] = useState(false);
  const load = useCallback(() => {
    setLoading(true);
    axios.get(`${API}/claims/report/lint`, { params: { product_id: productId } })
      .then((r) => setD(r.data)).catch(() => toast.error("Lint failed"))
      .finally(() => setLoading(false));
  }, [productId]);
  useEffect(() => { load(); }, [load]);
  const t = d?.totals || {};
  const groups = [
    ["missing_citations", "Missing citations", WarningCircle, "text-red-600"],
    ["unused_references", "Unused references", BookOpen, "text-amber-600"],
    ["duplicate_references", "Duplicate references", Copy, "text-blue-600"],
    ["invalid_doi", "Invalid DOI", Prohibit, "text-red-600"],
    ["broken_links", "Broken links", Warning, "text-red-600"],
    ["reference_mismatch", "Reference mismatch", Files, "text-amber-600"],
  ];
  return (
    <div data-testid="lint-panel" className="bg-white border border-slate-200">
      <div className="px-5 py-4 border-b border-slate-200 flex items-center justify-between">
        <div>
          <p className="font-heading font-bold text-sm">Report integrity checks</p>
          <p className="text-xs text-slate-500 mt-1">Runs across all claims for this product.</p>
        </div>
        <button data-testid="lint-refresh" onClick={load} disabled={loading}
          className="text-[11px] font-semibold text-slate-700 border border-slate-300 px-3 py-1.5 hover:bg-slate-50 disabled:opacity-50">
          {loading ? "Checking…" : "Re-run checks"}
        </button>
      </div>
      <div className="grid grid-cols-2 md:grid-cols-3 divide-x divide-y md:divide-y-0 divide-slate-100 border-b border-slate-200">
        {groups.map(([k, l, Icon, cls]) => (
          <div key={k} data-testid={`lint-tile-${k}`} className="p-4">
            <p className={`overline-label ${(t[k] || 0) > 0 ? cls : "text-slate-400"}`}>{l}</p>
            <p className={`font-heading font-extrabold text-2xl mt-1 ${(t[k] || 0) > 0 ? cls : "text-slate-400"}`}>
              <Icon size={14} className="inline-block mr-1 align-baseline" /> {t[k] || 0}
            </p>
          </div>
        ))}
      </div>
      {d && (
        <div className="px-5 py-3 space-y-3 text-[11px]">
          {d.missing_citations.length > 0 && <>
            <p className="overline-label text-slate-400">Missing citations</p>
            <ul className="space-y-1">{d.missing_citations.map((m) => (
              <li key={m.claim_id} className="text-slate-600 border-l-2 border-red-300 pl-2">
                <span className="text-red-600 font-semibold">{m.category}</span> — {m.statement}
              </li>
            ))}</ul>
          </>}
          {d.duplicate_references.length > 0 && <>
            <p className="overline-label text-slate-400 mt-2">Duplicate references</p>
            <ul className="space-y-1">{d.duplicate_references.map((x, i) => (
              <li key={i} className="text-slate-600 border-l-2 border-blue-300 pl-2">
                <code className="font-mono-data">{x.canonical_key}</code> — cited {x.occurrences.length}× ({x.occurrences.map((o) => o[0]).join(", ")})
              </li>
            ))}</ul>
          </>}
          {d.invalid_doi.length > 0 && <>
            <p className="overline-label text-slate-400 mt-2">Invalid DOI</p>
            <ul className="space-y-1">{d.invalid_doi.map((x, i) => (
              <li key={i} className="text-slate-600 border-l-2 border-red-300 pl-2">{x.doi} (claim <code>{x.claim_id}</code>)</li>
            ))}</ul>
          </>}
          {d.broken_links.length > 0 && <>
            <p className="overline-label text-slate-400 mt-2">Broken links</p>
            <ul className="space-y-1">{d.broken_links.map((x, i) => (
              <li key={i} className="text-slate-600 border-l-2 border-red-300 pl-2">{x.source_document} — <a href={x.url} className="text-blue-600 hover:underline">{x.url}</a></li>
            ))}</ul>
          </>}
          {d.reference_mismatch.length > 0 && <>
            <p className="overline-label text-slate-400 mt-2">Reference mismatch</p>
            <ul className="space-y-1">{d.reference_mismatch.map((x, i) => (
              <li key={i} className="text-slate-600 border-l-2 border-amber-300 pl-2">{x.reason} (claim <code>{x.claim_id}</code>)</li>
            ))}</ul>
          </>}
          {d.unused_references.length > 0 && <>
            <p className="overline-label text-slate-400 mt-2">Unused references</p>
            <ul className="space-y-1">{d.unused_references.map((x, i) => (
              <li key={i} className="text-slate-600 border-l-2 border-amber-300 pl-2">{x.name} · <code>{x.evidence_asset_id}</code></li>
            ))}</ul>
          </>}
        </div>
      )}
    </div>
  );
}

function ReferencesPanel({ productId }) {
  const [style, setStyle] = useState("vancouver");
  const [refs, setRefs] = useState(null);
  const load = useCallback(() => {
    axios.get(`${API}/claims/report/references`, { params: { product_id: productId, style } })
      .then((r) => setRefs(r.data)).catch(() => toast.error("References load failed"));
  }, [productId, style]);
  useEffect(() => { load(); }, [load]);
  return (
    <div data-testid="references-panel" className="bg-white border border-slate-200">
      <div className="px-5 py-4 border-b border-slate-200 flex items-center justify-between gap-4">
        <div>
          <p className="font-heading font-bold text-sm">Reference list</p>
          <p className="text-xs text-slate-500 mt-1">Automatically built from every source cited by a claim. Deduped and ordered by first author.</p>
        </div>
        <Select value={style} onValueChange={setStyle}>
          <SelectTrigger data-testid="ref-style-select" className="h-8 w-40 text-xs bg-white"><SelectValue /></SelectTrigger>
          <SelectContent className="bg-white">
            <SelectItem value="vancouver">Vancouver</SelectItem>
            <SelectItem value="harvard">Harvard</SelectItem>
            <SelectItem value="ama">AMA</SelectItem>
          </SelectContent>
        </Select>
      </div>
      <ol className="px-5 py-3 space-y-2 list-decimal list-inside text-xs">
        {refs === null && <li className="list-none text-slate-400">Loading references…</li>}
        {refs && refs.length === 0 && <li data-testid="refs-empty" className="list-none text-slate-400">No cited sources yet — link at least one claim to a source.</li>}
        {refs && refs.map((r) => (
          <li key={r.canonical_key} data-testid={`ref-${r.index}`} className="text-slate-700 leading-relaxed">
            <span className="font-mono-data text-[10px] text-slate-400 mr-1">[{r.index}]</span>
            {r.formatted}
            <span className="ml-2 text-[10px] text-slate-400">cited by {r.claim_ids.length} claim{r.claim_ids.length > 1 ? "s" : ""}</span>
            {r.citation_status && r.citation_status !== "verified" && (
              <StatusChip status={r.citation_status} />
            )}
          </li>
        ))}
      </ol>
    </div>
  );
}

export default function ClaimsPage({ embedded }) {
  const { productId, product } = useProduct();
  const [tab, setTab] = useState("claims");
  const [claims, setClaims] = useState([]);
  const [loading, setLoading] = useState(false);
  const [categoryFilter, setCategoryFilter] = useState("all");
  const [creating, setCreating] = useState(false);
  const [activeSource, setActiveSource] = useState(null); // {claim, source}
  const [activeClaim, setActiveClaim] = useState(null); // editing target

  const load = useCallback(() => {
    if (!productId) return;
    setLoading(true);
    axios.get(`${API}/claims`, { params: { product_id: productId } })
      .then((r) => setClaims(r.data || [])).catch(() => toast.error("Failed to load claims"))
      .finally(() => setLoading(false));
  }, [productId]);
  useEffect(() => { load(); }, [load]);

  useEffect(() => {
    if (activeSource) {
      const fresh = claims.find((c) => c.id === activeSource.claim.id);
      if (fresh) {
        const src = fresh.source_links.find((l) => l.id === activeSource.source.id);
        if (src) setActiveSource({ claim: fresh, source: src });
        else setActiveSource(null);
      }
    }
  }, [claims]);

  const visible = useMemo(
    () => categoryFilter === "all" ? claims : claims.filter((c) => c.category === categoryFilter),
    [claims, categoryFilter]);

  const stats = useMemo(() => ({
    total: claims.length,
    missing: claims.filter((c) => c.source_links.length === 0).length,
    verified: claims.filter((c) => c.source_links.length > 0 && c.source_links.every((l) => l.citation_status === "verified")).length,
  }), [claims]);

  return (
    <PageShell embedded={embedded} movedTo={{ label: "Evidence Library", to: "/console/evidence-library?tab=claims" }} pageId="claims" overline="Claim-to-Source · Prompt 10"
      title="Claim & Reference Manager">
      <div className="flex items-center gap-1 border-b border-slate-200">
        {[["claims", "Claims"], ["references", "Reference list"], ["lint", "Integrity checks"]].map(([id, label]) => (
          <button key={id} data-testid={`claims-tab-${id}`} onClick={() => setTab(id)}
            className={`text-xs font-semibold px-4 py-2.5 border-b-2 -mb-px transition-colors ${tab === id ? "border-slate-900 text-slate-900" : "border-transparent text-slate-400 hover:text-slate-600"}`}>
            {label}
          </button>
        ))}
      </div>

      {tab === "claims" && (
        <>
          <div data-testid="claims-stats" className="grid grid-cols-3 bg-white border border-slate-200 divide-x divide-slate-100">
            {[
              ["Claims", stats.total, "slate"],
              ["Fully verified", stats.verified, "green"],
              ["Missing citation", stats.missing, "red"],
            ].map(([l, v, tone]) => (
              <div key={l} className="px-4 py-3">
                <p className="overline-label text-slate-400">{l}</p>
                <p className={`font-heading font-extrabold text-2xl mt-0.5 ${tone === "red" && v > 0 ? "text-red-600" : tone === "green" ? "text-emerald-700" : ""}`}>{v}</p>
              </div>
            ))}
          </div>

          <div className="flex flex-wrap items-center gap-2">
            <button data-testid="filter-cat-all" onClick={() => setCategoryFilter("all")}
              className={`text-[10px] font-semibold px-2.5 py-1 border ${categoryFilter === "all" ? "bg-slate-900 text-white border-slate-900" : "bg-white border-slate-200 text-slate-500 hover:bg-slate-50"}`}>All ({claims.length})</button>
            {Object.entries(CATEGORY_LABEL).map(([k, l]) => {
              const n = claims.filter((c) => c.category === k).length;
              return (
                <button key={k} data-testid={`filter-cat-${k}`} onClick={() => setCategoryFilter(k)}
                  className={`text-[10px] font-semibold px-2.5 py-1 border ${categoryFilter === k ? "bg-slate-900 text-white border-slate-900" : "bg-white border-slate-200 text-slate-500 hover:bg-slate-50"}`}>{l} ({n})</button>
              );
            })}
            <button data-testid="new-claim-btn" onClick={() => setCreating((c) => !c)}
              className="ml-auto text-[11px] font-semibold text-white bg-slate-900 px-3 py-1.5 hover:bg-slate-800 flex items-center gap-1.5">
              <Plus size={11} /> {creating ? "Close form" : "New claim"}
            </button>
          </div>

          {creating && <ClaimForm productId={productId} onCancel={() => setCreating(false)} onSaved={() => { setCreating(false); load(); }} />}

          {loading ? (
            <div data-testid="claims-loading" className="py-20 text-center text-xs text-slate-400">Loading claims…</div>
          ) : visible.length === 0 ? (
            <div data-testid="claims-empty" className="py-16 text-center text-xs text-slate-400 border border-dashed border-slate-200">
              No claims yet for {product?.name || "this product"} — click New claim to link a report statement to its source.
            </div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
              {visible.map((c) => (
                <ClaimRow key={c.id} claim={c}
                  onOpen={setActiveClaim}
                  onOpenSource={(claim, source) => setActiveSource({ claim, source })} />
              ))}
            </div>
          )}
        </>
      )}

      {tab === "references" && <ReferencesPanel productId={productId} />}
      {tab === "lint" && <LintPanel productId={productId} />}

      {activeSource && (
        <SourcePanel productId={productId} claim={activeSource.claim} source={activeSource.source}
          onClose={() => setActiveSource(null)} onChange={load} />
      )}

      {activeClaim && (
        <div className="fixed inset-0 z-50 flex" onClick={() => setActiveClaim(null)}>
          <div className="flex-1 bg-black/40" />
          <div onClick={(e) => e.stopPropagation()} className="w-[520px] max-w-full bg-white border-l border-slate-200 overflow-y-auto p-5 space-y-3">
            <header className="flex items-start justify-between">
              <div>
                <p className="overline-label text-slate-400">Edit claim</p>
                <p className="font-heading font-bold text-sm mt-0.5">{CATEGORY_LABEL[activeClaim.category]}</p>
              </div>
              <button data-testid="edit-close" onClick={() => setActiveClaim(null)} className="text-slate-400 hover:text-slate-900"><X size={16} /></button>
            </header>
            <p className="text-xs text-slate-700 border-l-2 border-slate-300 pl-2 italic">&ldquo;{activeClaim.statement}&rdquo;</p>
            <p className="overline-label text-slate-400">Add another source</p>
            <MiniAddSource claimId={activeClaim.id} onDone={() => { setActiveClaim(null); load(); }} />
            <p className="overline-label text-slate-400 mt-3">Delete</p>
            <button data-testid="claim-delete" onClick={async () => {
              if (!window.confirm("Delete this claim?")) return;
              try { await axios.delete(`${API}/claims/${activeClaim.id}`); toast.success("Claim deleted"); setActiveClaim(null); load(); }
              catch { toast.error("Delete failed"); }
            }} className="text-[11px] font-semibold text-red-600 border border-red-200 px-3 py-1.5 hover:bg-red-50 flex items-center gap-1">
              <Trash size={11} /> Delete claim
            </button>
          </div>
        </div>
      )}
    </PageShell>
  );
}

function MiniAddSource({ claimId, onDone }) {
  const [link, setLink] = useState({ exact_passage: "", source_document: "", complete_reference: "", doi: "", source_url: "" });
  const [busy, setBusy] = useState(false);
  const submit = async () => {
    if (link.exact_passage.trim().length < 5 || !link.source_document || link.complete_reference.trim().length < 8) {
      return toast.error("Passage (5+), Source document, and Reference (8+) are required");
    }
    setBusy(true);
    try {
      await axios.post(`${API}/claims/${claimId}/sources`, link);
      toast.success("Source added"); onDone();
    } catch (e) { toast.error(e.response?.data?.detail || "Add failed"); }
    finally { setBusy(false); }
  };
  return (
    <div className="space-y-2">
      <Input data-testid="mini-source-doc" className="h-8 text-xs" placeholder="Source document" value={link.source_document} onChange={(e) => setLink({ ...link, source_document: e.target.value })} />
      <Textarea data-testid="mini-passage" rows={2} className="text-xs" placeholder="Exact passage" value={link.exact_passage} onChange={(e) => setLink({ ...link, exact_passage: e.target.value })} />
      <Textarea data-testid="mini-reference" rows={2} className="text-xs" placeholder="Complete reference" value={link.complete_reference} onChange={(e) => setLink({ ...link, complete_reference: e.target.value })} />
      <div className="grid grid-cols-2 gap-2">
        <Input className="h-8 text-xs" placeholder="DOI" value={link.doi} onChange={(e) => setLink({ ...link, doi: e.target.value })} />
        <Input className="h-8 text-xs" placeholder="Source URL" value={link.source_url} onChange={(e) => setLink({ ...link, source_url: e.target.value })} />
      </div>
      <button data-testid="mini-submit" onClick={submit} disabled={busy}
        className="text-[11px] font-semibold text-white bg-slate-900 px-3 py-1.5 hover:bg-slate-800 disabled:opacity-50 flex items-center gap-1.5">
        {busy ? <CircleNotch size={11} className="animate-spin" /> : <Plus size={11} />} Add source
      </button>
    </div>
  );
}
